import { type EventFilter } from "@/Modules/Chat/WebSocketManager";
import { type ChatStateWebSocket, MessageTypeWebSocket } from "@/Modules/Chat/Models/WebSocket";
import { type UserStore, useUserStore } from "@/Modules/User/Store";
import { type SubscriptionStore, useSubscriptionStore } from "@/Modules/Subscription/Store";
import type { Role } from "@/Common/Models/Domain/Role";
import type { ChatStore } from "@/Modules/Chat/Store";

import { useChatStore } from "@/Modules/Chat/Store";
import { transformChatApiToChatRoom } from "@/Modules/Chat/Transformers/Chat";

import { hasValue, isObject } from "@/Utils/Objects";
import { UserStatus } from "@/Common/Models/Domain/UserStatus";
import { isValidString } from "@/Utils/String";

export interface ChatStateExtraParams {
  userStore: UserStore;
  subscriptionStore: SubscriptionStore;
  chatStore: ChatStore;
}

export const isChatState = (message: unknown): message is ChatStateWebSocket => {
  return isObject(message) && message.type === MessageTypeWebSocket.CHAT;
};

export const chatStateHandler = (
  chatStateWebSocket: ChatStateWebSocket,
  {
    userStore = useUserStore(),
    subscriptionStore = useSubscriptionStore(),
    chatStore = useChatStore(),
  }: ChatStateExtraParams
): Promise<undefined> => {
  if (chatStateWebSocket.state === UserStatus.TRANSFER_PENDING && userStore.user) {
    userStore.user.status = UserStatus.TRANSFER_PENDING;
    userStore.user.isGuest = false;
  }

  if (userStore.user) {
    if (chatStateWebSocket.left) {
      deleteChatInChatRooms(chatStore, chatStateWebSocket);
    } else if (chatStateWebSocket.part.length > 0) {
      addNewChatInChatRooms(userStore.user.role, chatStore, chatStateWebSocket, userStore);
    }
  }

  if (
    chatStateWebSocket.state === UserStatus.ACTIVE &&
    userStore.user &&
    userStore.user.status !== UserStatus.ACTIVE
  ) {
    userStore.user.status = UserStatus.ACTIVE;
    userStore.setUser();
    subscriptionStore.setSubscriptions();
  }

  if (chatStateWebSocket.state === UserStatus.SUBSCRIPTION_FINISHED && userStore.user) {
    userStore.user.status = UserStatus.SUBSCRIPTION_FINISHED;
  }

  return Promise.resolve(undefined);
};

const addNewChatInChatRooms = (
  role: Role,
  chatStore: ChatStore,
  chatStateWebSocket: ChatStateWebSocket,
  userStore: UserStore
) => {
  const events = hasValue(chatStore.rooms[chatStateWebSocket.chat])
    ? chatStore.rooms[chatStateWebSocket.chat].events
    : [];

  const newChatRoom = transformChatApiToChatRoom(chatStateWebSocket, role, events, userStore.user!);

  chatStore.rooms = {
    ...chatStore.rooms,
    [chatStateWebSocket.chat]: newChatRoom,
  };

  if (!isValidString(chatStore.activeChatRoomId) || !hasValue(chatStore.getActiveRoom)) {
    chatStore.setActiveChatRoom(chatStateWebSocket.chat);
  } else if (chatStore.activeChatRoomId === chatStateWebSocket.chat) {
    chatStore.rooms[chatStateWebSocket.chat].isLoadingEvents = false;
  }
};

const deleteChatInChatRooms = (chatStore: ChatStore, chatStateWebSocket: ChatStateWebSocket) => {
  if (chatStore.activeChatRoomId === chatStateWebSocket.chat) {
    const nextChat = chatStore.chatRooms.find((room) => room.id !== chatStateWebSocket.chat);

    if (hasValue(nextChat)) {
      chatStore.setActiveChatRoom(nextChat.id);
    } else {
      chatStore.setActiveChatRoom();
    }
  }

  delete chatStore.rooms[chatStateWebSocket.chat];
};

export default {
  isType: isChatState,
  handler: chatStateHandler,
} as EventFilter<ChatStateExtraParams>;
